import { useState } from 'react'
import { Chrome, Download, CheckCircle2, Copy, FileText, ShieldCheck, ExternalLink } from 'lucide-react'

const SUPPORTED_EMRS = [
  'ModMed / EMA', 'athenahealth', 'Practice Fusion', 'DrChrono',
  'Kareo / Tebra', 'Elation Health', 'AdvancedMD', 'eClinicalWorks',
  'NextGen', 'Greenway', 'Veradigm',
]

const STEPS = [
  { title: 'Install the extension', body: 'Add the Dr. Scribey extension to Chrome (link above). Pin it from the puzzle (🧩) menu so it stays visible in your toolbar.' },
  { title: 'Sign in once', body: 'Click the Dr. Scribey icon and log in with the same account you use here. Your recent notes load automatically — no patient data is stored in the extension.' },
  { title: 'Open your EMR + the note', body: 'In your EMR tab, click the Dr. Scribey icon, pick the note you want, then click Smart Paste. A small toolbar appears at the top of your EMR page.' },
  { title: 'Paste into the chart', body: 'Click the EMR text field you want to fill, then Paste Full Note — or copy section by section (HPI, Assessment, Plan). Mark the visit "In EHR" back on the Visits page when done.' },
]

export function ExtensionPage() {
  const [copied, setCopied] = useState(false)
  const storeUrl = 'https://chromewebstore.google.com/detail/dr-scribey-%E2%80%94-ai-clinical/ooofhjhipjhikeednbieeiafkoincodh'

  return (
    <div className="max-w-3xl mx-auto pb-12">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-brand-500/15 text-brand-600 dark:text-brand-300 flex items-center justify-center">
          <Chrome size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Browser Extension</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Get your finished notes into your EMR — the last mile.</p>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-5">
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          Dr. Scribey does not auto-push into your EMR. The Chrome extension is a <strong>secure copy/paste helper</strong>: it shows your recent notes in a side panel and lets you paste them — whole or section-by-section — into any browser-based EMR field. No patient data is stored in the extension; it holds your login token only.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={storeUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium px-4 py-2 rounded-lg">
            <Download size={15} /> Add to Chrome <ExternalLink size={13} className="opacity-70" />
          </a>
          <button
            onClick={() => { navigator.clipboard.writeText(storeUrl); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
            className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium px-4 py-2 rounded-lg">
            {copied ? <CheckCircle2 size={15} className="text-emerald-500" /> : <Copy size={15} />}
            {copied ? 'Link copied' : 'Copy install link'}
          </button>
        </div>
      </div>

      <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">How it works</h2>
      <ol className="space-y-3">
        {STEPS.map((s, i) => (
          <li key={i} className="flex gap-3 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
            <div className="shrink-0 w-7 h-7 rounded-full bg-brand-500/15 text-brand-600 dark:text-brand-300 flex items-center justify-center text-sm font-semibold">{i + 1}</div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">{s.title}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center gap-2 mb-2 text-gray-900 dark:text-gray-100 font-medium"><FileText size={16} className="text-brand-500" /> Works with</div>
          <div className="flex flex-wrap gap-1.5">
            {SUPPORTED_EMRS.map((e) => (
              <span key={e} className="text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">{e}</span>
            ))}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Any browser-based EMR works via copy/paste, even if not listed.</p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center gap-2 mb-2 text-gray-900 dark:text-gray-100 font-medium"><ShieldCheck size={16} className="text-emerald-500" /> Privacy</div>
          <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1 list-disc list-inside">
            <li>No patient data stored in the extension</li>
            <li>Holds your login token only</li>
            <li>Notes are fetched live when you open the panel</li>
          </ul>
        </div>
      </div>

      <p className="mt-8 text-xs text-gray-400 dark:text-gray-500">
        Some EMRs block automated paste — in that case use “Copy” and paste manually. Questions? <a href="/support" className="text-brand-500 hover:underline">Contact support</a>.
      </p>
    </div>
  )
}
